import * as crypto from 'crypto';

const ALGORITHM = 'aes-256-gcm';
const HASH_NAME = 'sha512';
const IV_LENGTH = 12;
const ITERATION_COUNT = 65535;
const KEY_LENGTH = 32;
const SALT_LENGTH = 16;
const TAG_LENGTH = 16;
const ENCRYPTED_POSITION = SALT_LENGTH + IV_LENGTH;

interface GettKeyType {
  salt: Buffer
}

interface EncryptType {
  plainText: string;
}

interface DecryptType {
  cipherText: string
}

export class GCM {
  secret: crypto.BinaryLike;

  constructor(key?: string) {
    this.secret = key ?? Buffer.from(String(import.meta.env.VITE_CIPHER_SECRET_KEY)).toString();
  }

  getKey({ salt }: GettKeyType) {
    return crypto.pbkdf2Sync(
      this.secret,
      salt,
      ITERATION_COUNT,
      KEY_LENGTH,
      HASH_NAME,
    );
  }

  encrypt({ plainText }: EncryptType) {
    if (plainText === '') return plainText;

    const salt = crypto.randomBytes(SALT_LENGTH);
    const iv = crypto.randomBytes(IV_LENGTH);

    const key = this.getKey({ salt });

    const cipher = crypto.createCipheriv(ALGORITHM, key, iv);
    const encrypted = Buffer.concat([
      cipher.update(String(plainText), 'utf8'),
      cipher.final(),
    ]);

    const tag = cipher.getAuthTag();
    return Buffer.concat([salt, iv, encrypted, tag]).toString('base64');
  }

  decrypt({ cipherText }: DecryptType) {
    try {
      const stringValue = Buffer.from(String(cipherText), 'base64');

      const salt = stringValue.subarray(0, SALT_LENGTH);
      const iv = stringValue.subarray(SALT_LENGTH, ENCRYPTED_POSITION);
      const encrypted = stringValue.subarray(ENCRYPTED_POSITION, stringValue.length - TAG_LENGTH);
      const tag = stringValue.subarray(-TAG_LENGTH);

      const key = this.getKey({ salt });
      const decipher = crypto.createDecipheriv(ALGORITHM, key, iv);
      decipher.setAuthTag(tag);

      return decipher.update(encrypted) + decipher.final('utf8');
    } catch (error) {
      return cipherText;
    }
  }
}
